import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Copyright from '../internals/components/Copyright';
import ChartBatteryPacksByType from '../components/ChartBatteryPacksByType';
import CustomizedTreeView from '../components/CustomizedTreeView';
import BatteryDataGrid from '../components/BatteryDataGrid';
import HighlightedCard from '../components/HighlightedCard';
import PageViewsBarChart from '../components/PageViewsBarChart';
import SessionsChart from '../components/SessionsChart';
import StatCard from '../components/StatCard';
import BatteryMap from '../components/BatteryMap';
import Card from '@mui/material/Card';
import { useTheme } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import HistoryIcon from '@mui/icons-material/History';
import { capitalize, Chip } from '@mui/material';
import { formatTimeAgo } from '../internals/utils';
import { formatExactTime } from '../internals/utils';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';

const models = {
  'IGT Red': { 'src': '/assets/img/IGT Red.png', 'color': '#ff3780' },
  'IGT Red+': { 'src': '/assets/img/IGT Red.png', 'color': '#ff3780' },
  'IGT Blu': { 'src': '/assets/img/IGT Blu.png', 'color': '#46aef7' },
  'IGT Black 4842': { 'src': '/assets/img/IGT Black.png', 'color': '#5D6F7D' },
  'IGT Black': { 'src': '/assets/img/IGT Black.png', 'color': '#5D6F7D' },
};

export function BatteryDetails({ batteryDetails: { imei, tag, model, model_voltage, model_current, rated, status, added, bmsType }, loading }) {
  const theme = useTheme();
  const { id } = useParams(); // This will get the `id` from the URL
  return (
    <Grid
      container
      spacing={2}
      columns={12}
    >
      <Card variant="outlined" sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '180px' }}>
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ width: '35%', height: '100%' }}>
          {loading && (
            <CircularProgress />)}
          <img src={models[model] != null ? models[model].src : '/assets/img/Unknown Battery.png'} width="100%" height="100%" style={{ objectFit: 'contain', display: loading ? 'none' : 'block' }} />
        </Box>
        <Box>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            {tag}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: 16, color: theme.palette.text.secondary }}>
            Model: <span style={{ color: theme.palette.info.main }}>{model}</span></Typography>
          <Typography variant="body2" sx={{ fontSize: 16, color: theme.palette.text.secondary }}>
            Rated: <span style={{ color: theme.palette.info.main }}>{model_voltage}V, {model_current}Ah</span></Typography>
          <Typography variant="body2" sx={{ fontSize: 16, color: theme.palette.text.secondary }}>
            Status: <span style={{ color: theme.palette.success.main }}>Healthy</span></Typography>
          <Typography variant="body2" sx={{ fontSize: 16, color: theme.palette.text.secondary }}>
            BMS: <span style={{ color: theme.palette.info.main, textTransform: 'capitalize' }}>{bmsType}</span></Typography>
          <Typography variant="body2" sx={{ fontSize: 16, color: theme.palette.text.secondary }}>
            Added: <span style={{ color: theme.palette.info.main }}>{added}</span></Typography>
          {/* <Typography variant="body2" sx={{ fontSize: 16, color: theme.palette.text.secondary }}>
            IMEI: <span style={{ color: theme.palette.info.main }}>{id}</span></Typography> */}
        </Box></Card>

    </Grid >
  );
}

function Battery() {
  const initial_data = [
    {
      title: 'SoC',
      value: '--',
      interval: 'Last 30 days',
      trend: 'up',
      data: [],
    },
    {
      title: 'Voltage',
      value: '--',
      interval: 'Last 30 days',
      trend: 'down',
      data: [],
    },
    {
      title: 'Current',
      value: '--',
      interval: 'Last 30 days',
      trend: 'neutral',
      data: [],
    },
    {
      title: 'Avg. Battery Cell Voltage',
      value: '--',
      interval: 'Last 30 days',
      trend: 'up',
      data: [],
    },
    {
      title: 'Avg. Battery Cell Temperature',
      value: '--',
      interval: 'Last 30 days',
      trend: 'neutral',
      data: [],
    },
    {
      title: 'Remaining Charge capacity',
      value: '--',
      interval: 'Last 30 days',
      trend: 'neutral',
      data: [],
    },
    {
      title: 'Cycle Number',
      value: '--',
      interval: 'Last 30 days',
      trend: 'up',
      data: [],
    },
  ];
  const initial_details =
  {
    imei: '--',
    tag: '--',
    model: '--',
    rated: '--',
    status: '--',
    bmsType: '--',
    added: '--',
  };
  const initial_location =
  {
    lat: 28.370108,
    lng: 76.922603
  };
  const units = ['%', 'V', 'A', 'V', '°C', 'Ah', ''];
  const { id } = useParams(); // This will get the `id` from the URL
  const [data, setData] = useState(initial_data);
  const [details, setDetails] = useState(initial_details);
  const [location, setLocation] = useState(initial_location);
  const [timestamps, setTimestamps] = useState([]);
  const [geographical, setGeographical] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState(true);

  const handleToggle = async () => {
    const payload = isEnabled ? 'DISABLE' : 'ENABLE';
    try {
      const response = await fetch(`https://0h5n8j55d5.execute-api.ap-south-1.amazonaws.com/dev/battery-packs/${details.imei}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ command: payload }),
      });

      if (response.ok) {
        console.log(`Command sent successfully: ${payload}`);
        setIsEnabled(!isEnabled);
      } else {
        console.error('Failed to send Command:', response.statusText);
      }
    } catch (error) {
      console.error('Error sending Command:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('https://0h5n8j55d5.execute-api.ap-south-1.amazonaws.com/dev/battery-packs/' + id);
        const batteryDataResponse = await response.json();
        // console.log(batteryDataResponse);
        const updatedData = data.map((item, index) => ({ ...item }));
        const updatedDetails = JSON.parse(JSON.stringify(initial_details));
        const updatedLocation = JSON.parse(JSON.stringify(initial_location));
        updatedData[0].data = [];
        updatedData[1].data = [];
        updatedData[2].data = [];
        updatedData[3].data = [];
        updatedData[4].data = [];
        updatedData[5].data = [];
        updatedData[6].data = [];
        const updated_timestamps = [];
        if (batteryDataResponse.meta !== undefined) {
          for (const key in batteryDataResponse.meta) {
            switch (key) {
              case 'IMEI':
                updatedDetails.imei = batteryDataResponse.meta[key];
                break;
              case 'id':
                updatedDetails.tag = batteryDataResponse.meta[key];
                // updatedDetails.model = batteryDataResponse.meta[key].includes('BLU') ? 'IGT Blu' : 'IGT Red';
                // if (batteryDataResponse.meta[key].includes('4842')) updatedDetails.model = 'IGT Black 4842';
                break;
              case 'model_name':
                updatedDetails.model = batteryDataResponse.meta[key];
                break;
              // case 'battery_pack__asset_tag__type':
              //   break;
              case 'createdAt':
                updatedDetails.added = (new Date(batteryDataResponse.meta[key])).toLocaleDateString();
                break;
              case 'isActive':
                updatedDetails.status = batteryDataResponse.meta[key];
                break;
              case 'model_bms':
                updatedDetails.bmsType = batteryDataResponse.meta[key];
                break;
              default:
                updatedDetails[key] = batteryDataResponse.meta[key];
            }
          }
        }
        setDetails(updatedDetails);
        batteryDataResponse.electrical.forEach((electricalPacket, index) => {
          Object.keys(electricalPacket).forEach(key => {
            switch (key) {
              case 'SoC':
                updatedData[0].data.push(Number(electricalPacket[key]));
                break;
              case 'battery_pack_voltage':
                updatedData[1].data.push(Number(electricalPacket[key]));
                break;
              case 'battery_pack_current':
                updatedData[2].data.push(Number(electricalPacket[key]));
                break;
              case 'battery_cell_voltage_avg':
                updatedData[3].data.push(Number(electricalPacket[key]));
                break;
              case 'battery_cell_temperature_avg':
                updatedData[4].data.push(Number(electricalPacket[key]));
                break;
              case 'RCC':
                updatedData[5].data.push(Number(electricalPacket[key]));
                break;
              case 'CoC':
                updatedData[6].data.push(Number(electricalPacket[key]));
                break;
              case 'timestamp':
                updated_timestamps.push(electricalPacket[key]);
                break;
              default:
            }
          });
        });
        setGeographical(batteryDataResponse.geographical || []);
        const latestLocation = batteryDataResponse.geographical[0];
        if (latestLocation !== undefined) {
          updatedLocation.lat = Number(latestLocation.lat);
          updatedLocation.lng = Number(latestLocation.lng);
          updatedLocation.timestamp = latestLocation.timestamp;
        }
        updatedData.forEach((updatedDataObject, index) => {
          if (updatedDataObject && updatedDataObject.data.length > 0) {
            updatedDataObject.value = updatedDataObject.data[0] + units[index];
            updatedDataObject.data.reverse();
          }
        });
        updated_timestamps.reverse();
        setData(updatedData);
        setTimestamps(updated_timestamps);
        setLocation(updatedLocation);
        setLoading(false);
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, []);

  const handleHover = (timestamp, lat, lng) => {
    setLocation({ timestamp: timestamp, lat: Number(lat), lng: Number(lng) });
  };

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      {/* cards */}
      <Typography component="h2" variant="h6" sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          Overview&emsp;<Chip icon={<HistoryIcon />} label={formatTimeAgo(timestamps.at(-1))} />
        </span>
        {details.bmsType === 'aeidth' && (
          <Button
            variant="contained"
            color={isEnabled ? 'error' : 'success'}
            onClick={handleToggle}
            sx={{ padding: '4px 8px', height: '28px' }}
          >
            {isEnabled ? 'Disable' : 'Enable'}
          </Button>
        )}
      </Typography>
      <Grid
        container
        spacing={2}
        columns={12}
        sx={{ mb: (theme) => theme.spacing(2) }}
      >
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}><BatteryDetails batteryDetails={details} loading={loading} /></Grid>
        {data.map((card, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard card={card} timestamps={timestamps} />
          </Grid>
        ))}
      </Grid>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Location&emsp;<Chip icon={<HistoryIcon />} label={formatTimeAgo(location.timestamp)} />
      </Typography>
      <Grid container spacing={2} columns={12}>
        <Grid size={{ xs: 12, lg: 9 }} sx={{ overflow: 'hidden', borderRadius: '8px', border: '1px solid var(--template-palette-divider)', height: { xs: '400px', lg: 'unset' } }}>
          <BatteryMap position={location} imageSrc={models[details.model] != null ? models[details.model].src : '/assets/img/Unknown Battery.png'} pinColor={models[details.model] != null ? models[details.model].color : '#5d5d5d'} loading={loading} />
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }} >
          <Stack gap={2} position={'relative'} direction={{ xs: 'column', sm: 'row', lg: 'column' }}>
            <Box
              sx={{
                position: 'absolute',
                left: '29px', // Position the line in the left margin
                top: 0,
                bottom: 0, // Extend to the bottom of the List
                width: '2px',
                backgroundColor: 'var(--template-palette-divider)', // Line color
              }}
            />
            <List sx={{
              height: '500px', // Set a fixed height for the list
              overflow: 'auto',   // Enable scrolling when content overflows
              border: '1px solid var(--template-palette-divider)', // Optional: Add a border for better visibility
              borderRadius: '8px', // Optional: Add rounded corners
            }}>

              {loading && (
                <CircularProgress sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
              )}
              {geographical.length === 0 && !loading && (
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: 'center', // Center-align the text
                    color: 'text.secondary', // Optional: Use a secondary text color
                    fontStyle: 'italic', // Optional: Add italic styling
                    padding: 2, // Optional: Add padding for better spacing
                  }}
                >
                  No geographical data available
                </Typography>
                // "No geographical data available"
                // <ListItem>
                //   <ListItemText primary="" />
                // </ListItem>
              )}
              {geographical.map((geo, index) => (
                <ListItem
                  key={index}
                  onMouseOver={() => handleHover(geo.timestamp, geo.lat, geo.lng)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: '8px', // Add rounded corners to the selection rectangle
                    '&:hover': {
                      backgroundColor: 'var(--template-palette-action-hover)', // Change background color on hover
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: index === 0 ? 'success.main' : 'text.secondary', // Highlight the first item
                      marginRight: '20px', // Space between the circle and the text
                      marginTop: '8px', // Align circle with the text
                    }}
                  />
                  <ListItemText
                    primary={
                      <>
                        {formatExactTime(geo.timestamp)}
                        {index === 0 && (
                          <Chip
                            label="Latest"
                            size="small"
                            color="success"
                            sx={{ ml: 2, verticalAlign: 'middle' }} // Add margin and align with text
                          />
                        )}
                      </>
                    }
                    secondary={`(${geo.lat}, ${geo.lng})${geo.odo != null ? ` - Odo: ${geo.odo} Km` : ''}${geo.volt != null ? ` - ${geo.volt} V` : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          </Stack>
        </Grid>
      </Grid>
      <Copyright sx={{ my: 4 }} />
    </Box >
  );
};
export default React.memo(Battery);